import React, { useEffect, useState } from "react";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import TherapistSidebar from "../../components/TherapistSidebar";

const intervalOptions = [
  { label: "Săptămâna curentă", value: "this_week" },
  { label: "Săptămâna trecută", value: "last_week" },
  { label: "Luna curentă", value: "this_month" },
  { label: "Luna trecută", value: "last_month" },
  { label: "Interval custom", value: "custom" },
];

const getStartEndDates = (type: string) => {
  const today = new Date();
  let start: Date;
  let end: Date;

  const cloneDate = (date: Date) => new Date(date.getTime());
  const getMondayOfWeek = (date: Date) => {
    const cloned = cloneDate(date);
    const day = cloned.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    cloned.setDate(cloned.getDate() + diff);
    cloned.setHours(0, 0, 0, 0);
    return cloned;
  };

  if (type === "this_week") {
    start = getMondayOfWeek(today);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (type === "last_week") {
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    start = getMondayOfWeek(lastWeek);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (type === "this_month") {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  } else if (type === "last_month") {
    const year = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const month = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    start = new Date(year, month, 1);
    end = new Date(year, month + 1, 0);
  } else {
    start = cloneDate(today);
    end = cloneDate(today);
  }

  const toDateString = (d: Date, time: "start" | "end") => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const datePart = `${year}-${month}-${day}`;
    return time === "start"
      ? `${datePart}T00:00:00`
      : `${datePart}T23:59:59.999`;
  };

  return {
    start_time: toDateString(start, "start"),
    end_time: toDateString(end, "end"),
  };
};

interface ScoreEntry {
  count: number;
  score: number;
}

interface ScoreResponse {
  therapist_id: string;
  entries: Record<string, ScoreEntry>;
  total_score: number;
}

interface ItemType {
  key: string;
  value: string;
}

const TherapistScorePage: React.FC = () => {
  const { token, userId } = useAuth();
  const [scoreData, setScoreData] = useState<ScoreResponse | null>(null);
  const [intervalType, setIntervalType] = useState("this_week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [therapyTypes, setTherapyTypes] = useState<ItemType[]>([]);
  const [activities, setActivities] = useState<ItemType[]>([]);


  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const therapyRes = await fetch(`${API_BASE_URL}/types/therapy-type`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activityRes = await fetch(`${API_BASE_URL}/types/activities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const therapyData = await therapyRes.json();
        const activityData = await activityRes.json();
        setTherapyTypes(therapyData.data || []);
        setActivities(activityData.data || []);
      } catch (err) {
        console.error("Eroare la încărcarea tipurilor:", err);
      }
    };

    fetchTypes();
  }, [token]);

  const fetchScore = async () => {
    if (!userId) return;

    let start_time = "", end_time = "";
    if (intervalType === "custom") {
      if (!customStart || !customEnd) return;
      start_time = `${customStart}T00:00:00`;
      end_time = `${customEnd}T23:59:59.999`;
    } else {
      const range = getStartEndDates(intervalType);
      start_time = range.start_time;
      end_time = range.end_time;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/score-config/total-by-therapist?start_time=${start_time}&end_time=${end_time}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      if (Array.isArray(data)) {
        const entry = data.find((d: ScoreResponse) => d.therapist_id === userId);
        setScoreData(entry || null);
      } else {
        console.error("Răspuns invalid:", data);
        setScoreData(null);
      }
    } catch (err) {
      console.error("Eroare la fetchScore:", err);
      setScoreData(null);
    }
  };

  useEffect(() => {
    fetchScore();
  }, [intervalType, customStart, customEnd]);

  const allItems = [...therapyTypes, ...activities];
  const entries = scoreData?.entries || {};

  // construim lista din entries (ce a returnat backend-ul), nu din listele de tipuri
  const filteredItems = Object.keys(entries).map((key) => ({
    key,
    value: allItems.find((i) => i.key === key)?.value || key,
  }));
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsedTherapist") === "true";
  });
  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <TherapistSidebar onCollapseChange={setIsCollapsed} />
      <div className="page-content">
        <h1 className="page-title">Punctajul meu</h1>

        <div className="interval-container">
          <label>Interval: </label>
          <select value={intervalType} onChange={(e) => setIntervalType(e.target.value)}>
            {intervalOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {intervalType === "custom" && (
            <>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
            </>
          )}
        </div>

        <div className="table-rounded-container">
          <table className="table-rounded table-therapist-points">
            <thead>
              <tr>
                <th>Terapie/Activitate</th>
                <th className="text-right">Punctaj obținut</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <tr key={item.key}>
                    <td>{item.value}</td>
                    <td className="text-right">{entries[item.key]?.score || 0} puncte</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2}>Nu au fost efectuate activități/terapii.</td>
                </tr>
              )}
              {scoreData && (
                <tr className="total-therapist">
                  <td><strong>Punctaj total</strong></td>
                  <td className="text-right"><strong>{scoreData.total_score} puncte</strong></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TherapistScorePage;
